import {
	FormItem,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { AlertManager, useAlerts } from "client/Hooks";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	FolderIdentifierModel,
	SpecificFolderIdentifierModel,
	WellKnownFolder,
	WellKnownFolderIdentifierModel
} from "client/Api/Workspace/LiveInformation/Email/emailLiveInformation";
import { MailBoxKind } from "./EditEmailTabItem";
import {
	getWellKnownFolderName,
	WELLKNOWN_FOLDERS,
} from "./EditEmailTile";
import { getNumericEnumValues } from "client/Misc/utils";
import { useSelectFolderInMailboxDialog } from "client/Features/Email/Dialogs/SelectFolderInMailboxDialog";

type FolderInputProps = {
	currentMailbox: string;
	currentMailboxKind: MailBoxKind;
	attachable: IValueInputAttachable<
		WellKnownFolderIdentifierModel
		| FolderIdentifierModel
		| null
	>;
};

const SELECT_OTHER_FOLDER = "SelectOtherFolder";
type FolderOptions = SpecificFolderIdentifierModel
	| WellKnownFolderIdentifierModel
	| typeof SELECT_OTHER_FOLDER;

export const FolderInput = (props: FolderInputProps): ReactElement => {
	const {
		currentMailbox,
		currentMailboxKind,
		attachable
	} = props;
	const alertManager: AlertManager = useAlerts();

	const [selectFolderInMailboxDialog, openSelectFolderInMailboxDialog] = useSelectFolderInMailboxDialog();

	const attachableValue: WellKnownFolderIdentifierModel | FolderIdentifierModel | null = useMemo(
		() => attachable.value,
		[attachable.value]
	);

	const getFolderName = useCallback(
		(folder: WellKnownFolderIdentifierModel | SpecificFolderIdentifierModel): string => {
			if (isWellknownFolderIdentifier(folder)) {
				return getWellKnownFolderName(folder);
			}
			return folder?.name ?? "";
		},
		[]
	);

	const selectedOption = useMemo(
		(): Exclude<FolderOptions, typeof SELECT_OTHER_FOLDER> | nil => {
			return isWellknownFolderIdentifier(attachableValue)
				? attachableValue
				: attachableValue?.specific
					? attachableValue?.specific
					: attachableValue?.wellKnown ;
		},
		[attachableValue]
	);

	const options = useMemo(
		(): SelectItem<FolderOptions>[] => {
			const options: SelectItem<FolderOptions>[] = [];

			const wellknownFolderIdentifierOptions: SelectItem<WellKnownFolderIdentifierModel>[] = getNumericEnumValues(WellKnownFolder)
				.map((folder) => {
					const value: WellKnownFolderIdentifierModel = WELLKNOWN_FOLDERS[folder];

					return {
						text: getWellKnownFolderName(value),
						value
					};
				});

			if (currentMailboxKind === "user") {
				return wellknownFolderIdentifierOptions;
			}

			options.push(
				...wellknownFolderIdentifierOptions
			);

			if (selectedOption && !isWellknownFolderIdentifier(selectedOption)) {
				options.push({
					text: getFolderName(selectedOption),
					value: selectedOption
				});
			}

			options.push({
				text: localize("_SelectOther"),
				value: SELECT_OTHER_FOLDER
			});

			return options;
		},
		[
			currentMailboxKind,
			getFolderName,
			selectedOption
		]
	);

	const folderSelectChangeHandler = useCallback(
		async (newValue: FolderOptions): Promise<void> => {
			if (newValue === SELECT_OTHER_FOLDER) {
				if (currentMailbox === "") {
					alertManager.showError(
						localize("_PleaseFillInAMailbox")
					);
					return;
				}

				const selectFolderResult = await openSelectFolderInMailboxDialog({
					mailbox: {
						smtpAddress: currentMailbox.trim(),
						name: currentMailbox.trim()
					}
				});

				if (selectFolderResult.wasSuccessful) {
					const {
						id,
						name,
					} = selectFolderResult.result;

					folderSelectChangeHandler({ id, name });
				}

				return;
			}

			if (currentMailboxKind === "user" && isWellknownFolderIdentifier(newValue)) {
				attachable.attachToSelect()
					.onChange(newValue);
			} else {
				attachable.attachToSelect()
					.onChange(
						isWellknownFolderIdentifier(newValue)
							? { wellKnown: newValue }
							: { specific: newValue }
					);
			}
		},
		[
			currentMailboxKind,
			currentMailbox,
			openSelectFolderInMailboxDialog,
			alertManager,
			attachable
		]
	);

	return 	(
		<FormItem label={localize("_Folder")}>
			<Select
				{...attachable.attachToSelect()}
				value={selectedOption}
				options={options}
				onChange={folderSelectChangeHandler}
				menuTitle={localize("_Folder")}
			/>
			<ValidationError
				{...attachable.attachToValidationError()}
			/>
			{selectFolderInMailboxDialog}
		</FormItem>
	);
};

FolderInput.displayName = "FolderInput";

function isWellknownFolderIdentifier(
	folder: any
): folder is WellKnownFolderIdentifierModel {
	return getNumericEnumValues(WellKnownFolder)
		.map((x): WellKnownFolderIdentifierModel => WELLKNOWN_FOLDERS[x])
		.includes(folder);
}
